import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AsyncValidatorFn, FormBuilder, Validators } from '@angular/forms';
import { combineLatest, distinctUntilChanged, Observable, switchMap, throttleTime } from 'rxjs';
import { DataTableDirective } from '../../../shared/directives/data-table.directive';
import { ColumnConfig } from '../../../shared/models/column-config.model';
import { ResultSet } from '../../../shared/models/result-set.model';
import { existsValidator } from '../../../shared/validators/exists.validator';
import { TitleService } from '../../../shared/services/title.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { PageStateManagerService } from '../../../shared/services/page-state-manager.service';
import { AuthService } from '../../../shared/services/auth.service';
import { WaitingListApplicant, WaitingListApplicantStatus } from '../models/waiting-list-applicant.model';
import { WaitingListApplicantsService } from '../waiting-list-applicants.service';

@Component({
  selector: 'app-waiting-list-applicants-list',
  templateUrl: './waiting-list-applicants-list.component.html',
  standalone: false
})
export class WaitingListApplicantsListComponent extends DataTableDirective<WaitingListApplicant> implements OnInit {

  override columns: ColumnConfig[] = [];
  override modelName = 'Waiting List Applicant';
  moveActionItemId: number | null = null;
  convertActionItemId: number | null = null;
  private confirmConvertItemId: number | null = null;
  notesItem: WaitingListApplicant | null = null;
  readonly statusOptions: { value: WaitingListApplicantStatus; label: string }[] = [
    { value: 'Active', label: 'Active' },
    { value: 'Suspended', label: 'Suspended' },
    { value: 'Joined', label: 'Joined' },
  ];
  selectedStatus: WaitingListApplicantStatus = 'Active';

  constructor(
    private waitingListApplicantsService: WaitingListApplicantsService,
    fb: FormBuilder,
    route: ActivatedRoute,
    router: Router,
    titleService: TitleService,
    notificationService: NotificationService,
    pageStateManager: PageStateManagerService,
    authService: AuthService,
  ) {
    super(
      waitingListApplicantsService,
      fb,
      route,
      router,
      titleService,
      notificationService,
      pageStateManager,
      authService
    );
  }

  override ngOnInit() {
    this.columns = [
      {
        name: 'position',
        label: 'Position',
        inputType: 'read-only',
      },
      {
        name: 'status',
        label: 'Status',
        inputType: 'select',
        validators: [Validators.required],
        options: [
          { value: 'Active', label: 'Active' },
          { value: 'Suspended', label: 'Suspended' },
          { value: 'Joined', label: 'Joined' },
        ],
        errorMessages: { required: 'Status is required' }
      },
      {
        name: 'firstName',
        label: 'First Name',
        inputType: 'text',
        validators: [Validators.required],
        errorMessages: { required: 'First Name is required' }
      },
      {
        name: 'lastName',
        label: 'Last Name',
        inputType: 'text',
        validators: [Validators.required],
        errorMessages: { required: 'Last Name is required' }
      },
      {
        name: 'email',
        label: 'Email',
        inputType: 'email',
        validators: [Validators.required, Validators.email],
        asyncValidators: [existsValidator(this.waitingListApplicantsService)],
        updateOn: 'blur',
        errorMessages: {
          required: 'Email is required',
          email: 'Please enter a valid email address',
          exists: 'A waiting list applicant with this email address already exists'
        }
      },
      {
        name: 'address',
        label: 'Address',
        inputType: 'text',
        errorMessages: {}
      },
      {
        name: 'town',
        label: 'Town',
        inputType: 'text',
        errorMessages: {}
      },
      {
        name: 'postCode',
        label: 'Post Code',
        inputType: 'text',
        validators: [
          Validators.pattern('^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$'),
        ],
        errorMessages: {
          pattern: 'Please enter a valid UK Post Code'
        }
      },
      {
        name: 'phoneNumber',
        label: 'Phone',
        inputType: 'text',
        validators: [Validators.required],
        errorMessages: { required: 'Phone Number is required' }
      },
      {
        name: 'createdAt',
        label: 'Date Applied',
        inputType: 'read-only',
      }
    ];

    super.ngOnInit();
  }

  protected override getColumnsForCreate(): ColumnConfig[] {
    return this.columns.filter((column) => column.name !== 'position' && column.name !== 'status' && column.name !== 'createdAt' && column.inputType !== 'link');
  }

  protected override getAsyncValidatorsForEdit(column: ColumnConfig, itemId: number): AsyncValidatorFn[] {
    if (column.name === 'email') {
      return [existsValidator(this.waitingListApplicantsService, itemId)];
    }
    return [];
  }

  moveUp(itemId: number): void {
    this.move(itemId, 'up');
  }

  moveDown(itemId: number): void {
    this.move(itemId, 'down');
  }

  confirmConvertToMember(itemId: number): void {
    if (this.isCustomActionInProgress()) {
      return;
    }

    this.confirmConvertItemId = itemId;
    this.confirmMessage = 'Are you sure you want to convert this applicant to a member?';
    this.showConfirmDialog = true;
  }

  isFirstInList(item: WaitingListApplicant): boolean {
    if (!this.canMove(item) || item.position === null) {
      return true;
    }
    return item.position <= 1;
  }

  isLastInList(item: WaitingListApplicant): boolean {
    if (!this.canMove(item) || item.position === null) {
      return true;
    }
    if (!this.totalItems) {
      return false;
    }
    return item.position >= this.totalItems;
  }

  canMove(item: WaitingListApplicant): boolean {
    return item.status === 'Active' && item.position !== null;
  }

  canConvert(item: WaitingListApplicant): boolean {
    return item.status !== 'Joined' && this.missingMemberFields(item).length === 0;
  }

  convertTitle(item: WaitingListApplicant): string {
    const missing = this.missingMemberFields(item);
    if (item.status !== 'Joined' && missing.length > 0) {
      return `Complete ${missing.join(', ')} before converting to member`;
    }
    return 'Convert to Member';
  }

  onStatusFilterChange(value: WaitingListApplicantStatus): void {
    this.selectedStatus = value;
    this.onPageChange(1);
  }

  protected override buildResultSet$(): Observable<ResultSet<WaitingListApplicant>> {
    return combineLatest([
      this.currentPageSubject,
      this.pageSizeSubject.pipe(distinctUntilChanged()),
      this.searchTermSubject.pipe(distinctUntilChanged())
    ]).pipe(
      throttleTime(300, undefined, { leading: true, trailing: true }),
      switchMap(([page, size, search]) => {
        const offset = (page - 1) * size;
        const statuses: WaitingListApplicantStatus[] = [this.selectedStatus];
        return search
          ? this.waitingListApplicantsService.searchByStatus(search, statuses, offset, size)
          : this.waitingListApplicantsService.getAllByStatus(statuses, offset, size);
      })
    );
  }

  override handleConfirm(): void {
    if (this.confirmConvertItemId !== null) {
      const itemId = this.confirmConvertItemId;
      this.confirmConvertItemId = null;
      this.showConfirmDialog = false;
      this.convertToMember(itemId);
      return;
    }

    super.handleConfirm();
  }

  override handleCancel(): void {
    this.confirmConvertItemId = null;
    super.handleCancel();
  }

  openNotes(item: WaitingListApplicant): void {
    this.notesItem = item;
  }

  onNotesSave(notes: string | null): void {
    if (!this.notesItem) return;
    const updated = { ...this.notesItem, notes };
    this.waitingListApplicantsService.update(updated).subscribe({
      next: (saved) => {
        this.notesItem!.notes = saved.notes;
        this.notesItem = null;
        this.notificationService.success('Notes saved successfully');
      },
      error: () => {
        this.notesItem = null;
      }
    });
  }

  onNotesCancel(): void {
    this.notesItem = null;
  }

  private missingMemberFields(item: WaitingListApplicant): string[] {
    const requiredFields: { key: 'address' | 'town' | 'postCode'; label: string }[] = [
      { key: 'address', label: 'Address' },
      { key: 'town', label: 'Town' },
      { key: 'postCode', label: 'Post Code' },
    ];
    return requiredFields
      .filter(({ key }) => !item[key]?.trim())
      .map(({ label }) => label);
  }

  private isCustomActionInProgress(): boolean {
    return this.moveActionItemId !== null || this.convertActionItemId !== null;
  }

  private move(itemId: number, direction: 'up' | 'down'): void {
    if (this.isCustomActionInProgress()) {
      return;
    }

    this.moveActionItemId = itemId;
    const moveRequest$ = direction === 'up'
      ? this.waitingListApplicantsService.moveUp(itemId)
      : this.waitingListApplicantsService.moveDown(itemId);

    moveRequest$.subscribe({
      next: () => {
        this.notificationService.success(`Applicant moved ${direction} successfully`);
        this.currentPageSubject.next(this.currentPage);
      },
      error: () => {
        this.moveActionItemId = null;
      },
      complete: () => {
        this.moveActionItemId = null;
      }
    });
  }

  private convertToMember(itemId: number): void {
    this.convertActionItemId = itemId;

    this.waitingListApplicantsService.convertToMember(itemId).subscribe({
      next: () => {
        this.notificationService.success('Applicant converted to member successfully');
        this.currentPageSubject.next(this.currentPage);
      },
      error: () => {
        this.convertActionItemId = null;
      },
      complete: () => {
        this.convertActionItemId = null;
      }
    });
  }
}